/* ISPISI TAHOGRAFA sa telefona (task 108, 27.10.2026).
   Vozač slika ispis sa tahografa, upiše dan prekršaja i opis. Ovde ih kancelarija
   pregleda i označava kao pregledane; isti ispisi u PREGLED-u boje prekršaj tog
   vozača i dana u zeleno (objašnjen). */
import React, { useState, useEffect, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Tooltip } from 'primereact/tooltip';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import Loading from '../../Loading/loading';
import { AuthContext } from '../../Header/auth - context';
import useVisinaTabele from "../../custom/visinaTabele";
import './Kazne.css';

/* Link do fajla — ista konvencija kao u evidenciji kazni. */
export const putanjaFajla = (p) => {
    if (!p) return null;
    const norm = String(p).replace(/\\/g, '/').replace(/^C:\/Dokumenta\//, '');
    return `${process.env.REACT_APP_BACKEND_URL}/files/${encodeURI(norm.replace(/^\//, ''))}`;
};

const toISO = (d) => d instanceof Date
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    : null;

const KazneIspisi = () => {
    const { t } = useTranslation();
    const toast = useRef(null);
    const auth = useContext(AuthContext);
    const company = useSelector(state => state.company);

    const [load, setLoad] = useState(false);
    const [od, setOd] = useState(new Date(Date.now() - 30 * 86400000));
    const [doDatuma, setDoDatuma] = useState(new Date());
    const [podaci, setPodaci] = useState([]);
    const [globalFilter, setGlobalFilter] = useState(null);
    /* Spisak se zavrsava na dnu ekrana; visina se MERI, ne pogadja. */
    const [omotTabele, visinaTabele, redovaTabele] = useVisinaTabele([podaci, globalFilter]);

    const poziv = async (putanja, body) => {
        const r = await fetch(process.env.REACT_APP_BACKEND_URL + putanja, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
            body: JSON.stringify(body),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.message || t('kazneIspisi.greska'));
        return data;
    };

    const ucitaj = async () => {
        setLoad(true);
        try {
            const data = await poziv('/tacho/ispisi', { step: 'WEB_GET', od: toISO(od), do: toISO(doDatuma) });
            setPodaci(data.IspisiDB || []);
        } catch (e) {
            toast.current.show({ severity: 'error', summary: t('common.greska'), detail: e.message, life: 4000 });
        }
        setLoad(false);
    };

    useEffect(() => { ucitaj(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const promeniPregledano = async (row) => {
        setLoad(true);
        try {
            const r = await poziv('/tacho/ispisi', { step: 'WEB_SAVE_PREGLEDANO', id: row.id, pregledano: row.Pregledano ? 0 : 1 });
            toast.current.show({ severity: 'success', summary: t('common.uspeh'), detail: r.message, life: 2500 });
            await ucitaj();
        } catch (e) {
            toast.current.show({ severity: 'error', summary: t('common.greska'), detail: e.message, life: 4000 });
        }
        setLoad(false);
    };

    const obrisi = (row) => {
        confirmDialog({
            message: t('kazneIspisi.obrisiPitanje', { vozac: row.Vozac, datum: row.DatumPrikaz }),
            header: t('kazneIspisi.obrisi'),
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                setLoad(true);
                try {
                    const r = await poziv('/tacho/deleteIspis', { id: row.id });
                    toast.current.show({ severity: 'success', summary: t('common.uspeh'), detail: r.message, life: 2500 });
                    await ucitaj();
                } catch (e) {
                    toast.current.show({ severity: 'error', summary: t('common.greska'), detail: e.message, life: 4000 });
                }
                setLoad(false);
            },
        });
    };

    const exportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Ispisi');
        const headers = [t('kazneIspisi.datum'), t('ture.vozac'), t('kazneIspisi.opis'), t('kazneIspisi.tura'),
            t('kazneIspisi.poslato'), t('kazneIspisi.stanje')];
        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true };
        headerRow.eachCell(cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } };
            cell.border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
        });
        (podaci || []).forEach(r => {
            const row = worksheet.addRow([r.DatumPrikaz, r.Vozac, r.Opis, r.Tura || '', r.Poslato,
                r.Pregledano ? `${t('kazneIspisi.pregledano')} — ${r.Pregledao || ''} ${r.DatumPregleda || ''}` : t('kazneIspisi.cekaPregled')]);
            row.eachCell(cell => {
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
        });
        worksheet.columns.forEach(col => {
            let max = 10;
            col.eachCell(cell => { if (cell.value) max = Math.max(max, cell.value.toString().length); });
            col.width = Math.min(max + 2, 80);
        });
        const dt = new Date().toLocaleString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Belgrade' }).replace(/,/g, '');
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
            `Ispisi tahografa - ${company?.companyName || ''} - ${dt}.xlsx`);
    };

    const tableHeader = (
        <div className="flex align-items-center gap-2 export-buttons" style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
                <Button type="button" icon="pi pi-file-excel" severity="success" rounded onClick={exportExcel} data-pr-tooltip="XLS" />
            </div>
            <input type="search" placeholder={t('common.pretraga')} className="p-inputtext p-component"
                onInput={e => setGlobalFilter(e.target.value)} />
        </div>
    );

    const slikaBody = (row) => {
        const lista = String(row.Fajlovi || '').split('|').filter(Boolean);
        if (!lista.length) return '—';
        return lista.map((p, i) => (
            <Button key={i} icon="pi pi-eye" label={t('kazneIspisi.slika')} type="button"
                className="p-button-text p-button-sm kazne-fajl-link"
                onClick={() => window.open(putanjaFajla(p), '_blank')} />
        ));
    };

    const stanjeBody = (row) => (
        <div className="ispis-stanje">
            {row.Pregledano
                ? <span className="ispis-pregledano"><i className="pi pi-check" /> {t('kazneIspisi.pregledano')}
                    {row.Pregledao ? ` · ${row.Pregledao}` : ''}{row.DatumPregleda ? ` · ${row.DatumPregleda}` : ''}</span>
                : <span className="ispis-ceka"><i className="pi pi-clock" /> {t('kazneIspisi.cekaPregled')}</span>}
        </div>
    );

    const akcijeBody = (row) => (
        <div style={{ display: 'flex', gap: '0.3rem' }}>
            <Button type="button" size="small" severity={row.Pregledano ? 'secondary' : 'success'}
                icon={row.Pregledano ? 'pi pi-undo' : 'pi pi-check'}
                label={row.Pregledano ? t('kazneIspisi.vrati') : t('kazneIspisi.oznaciPregledano')}
                onClick={() => promeniPregledano(row)} />
            <Button type="button" size="small" severity="danger" icon="pi pi-trash" text
                onClick={() => obrisi(row)} tooltip={t('kazneIspisi.obrisi')} tooltipOptions={{ position: 'top' }} />
        </div>
    );

    return (
        <div style={{ marginTop: '1%' }}>
            {load && <Loading />}
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="col-12">
                <div className="blagajnaDashboard dnevniceDashboard">
                    <Card>
                        <div className="kazne-izv-filter-row">
                            <label className="kazne-izv-label">{t('mk.odDatuma')}</label>
                            <Calendar value={od} onChange={e => setOd(e.value)} showIcon dateFormat="dd.mm.yy" mask="99.99.9999" />
                            <label className="kazne-izv-label">{t('mk.doDatuma')}</label>
                            <Calendar value={doDatuma} onChange={e => setDoDatuma(e.value)} showIcon dateFormat="dd.mm.yy" mask="99.99.9999" />
                            <Button label={t('mk.prikazi')} icon="pi pi-search" onClick={ucitaj} className="p-button p-button-primary" />
                        </div>
                        <div className="kazne-iznosi-napomena">{t('kazneIspisi.savet')}</div>
                    </Card>
                    <Card>
                        <Tooltip target=".export-buttons>button" position="bottom" />
                        <div ref={omotTabele}>
                        <DataTable
                            value={podaci || []}
                            paginator rows={redovaTabele}
                            globalFilter={globalFilter}
                            scrollable scrollHeight={visinaTabele}
                            header={tableHeader}
                            resizableColumns columnResizeMode="expand"
                            className="turePregledTable"
                            emptyMessage={t('kazneIspisi.nema')}
                            rowClassName={r => (r.Pregledano ? {} : { 'ispis-red-novi': true })}>
                            <Column field="DatumPrikaz" header={t('kazneIspisi.datum')} sortable sortField="Datum" style={{ minWidth: '110px' }} />
                            <Column field="Vozac" header={t('ture.vozac')} sortable style={{ minWidth: '160px' }} />
                            <Column field="Opis" header={t('kazneIspisi.opis')} style={{ minWidth: '300px' }} />
                            <Column field="Tura" header={t('kazneIspisi.tura')} sortable style={{ minWidth: '120px' }} />
                            <Column field="Poslato" header={t('kazneIspisi.poslato')} style={{ minWidth: '140px' }} />
                            <Column header={t('kazneIspisi.slika')} body={slikaBody} style={{ minWidth: '110px' }} />
                            <Column header={t('kazneIspisi.stanje')} body={stanjeBody} style={{ minWidth: '200px' }} />
                            <Column header="" body={akcijeBody} style={{ minWidth: '210px' }} />
                        </DataTable>
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default KazneIspisi;
